import React from 'react';
import { TrendingUp, ArrowUp, ChevronUp } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';

interface CreditRatingsPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

// Current ratings data (verified December 2025)
// Sources: S&P Global, Fitch Ratings, Moody's official announcements
// 2025: Major year for Uzbekistan - 2 upgrades + 1 outlook improvement!
const RATINGS_DATA = {
  sp: {
    rating: 'BB',
    outlook: 'Stable',
    lastUpdate: 'Nov 2025',
    previousRating: 'BB-',
    change: 'upgrade',
    agencyName: 'S&P',
    color: '#ef4444', // S&P red
  },
  fitch: {
    rating: 'BB',
    outlook: 'Stable',
    lastUpdate: 'Jun 2025',
    previousRating: 'BB-',
    change: 'upgrade',
    agencyName: 'Fitch',
    color: '#3b82f6', // Fitch blue
  },
  moodys: {
    rating: 'Ba3',
    outlook: 'Positive',
    lastUpdate: 'Jun 2025',
    previousRating: 'Ba3',
    change: 'outlook',
    agencyName: "Moody's",
    color: '#8b5cf6', // Moody's purple
  },
};

export const CreditRatingsPanel: React.FC<CreditRatingsPanelProps> = ({
  isMinimized,
  onMinimize,
}) => {
  const agencies = [
    { key: 'sp', ...RATINGS_DATA.sp },
    { key: 'fitch', ...RATINGS_DATA.fitch },
    { key: 'moodys', ...RATINGS_DATA.moodys },
  ];

  const exportData = agencies.map(a => ({
    agency: a.agencyName,
    rating: a.rating,
    outlook: a.outlook,
    previousRating: a.previousRating,
    lastUpdate: a.lastUpdate,
  }));

  return (
    <Panel
      id="credit-ratings"
      title="Credit Ratings"
      subtitle="Uzbekistan"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      data={exportData}
    >
      <div className="h-full flex flex-col p-3">
        {/* Header - Upgrade Celebration */}
        <div className="flex items-center justify-center gap-3 mb-4 py-2 bg-data-green/10 rounded-lg border border-data-green/20">
          <TrendingUp size={18} className="text-data-green" />
          <span className="text-sm font-semibold text-data-green tracking-wide">
            2 UPGRADES IN 2025
          </span>
          <ChevronUp size={16} className="text-data-green" />
        </div>

        {/* Rating Cards - Horizontal Layout */}
        <div className="flex-1 grid grid-cols-3 gap-3">
          {agencies.map(agency => (
            <div
              key={agency.key}
              className="flex flex-col items-center justify-center p-3 bg-terminal-border/40 rounded-lg border border-terminal-border relative overflow-hidden"
            >
              {/* Upgrade indicator glow */}
              {agency.change === 'upgrade' && (
                <div className="absolute inset-0 bg-gradient-to-t from-data-green/5 to-transparent" />
              )}

              {/* Agency Name */}
              <div
                className="text-xs font-bold tracking-wider mb-2 uppercase"
                style={{ color: agency.color }}
              >
                {agency.agencyName}
              </div>

              {/* Rating - HERO SIZE */}
              <div className="text-3xl md:text-4xl font-mono font-black text-white mb-1">
                {agency.rating}
              </div>

              {/* Outlook Badge */}
              <div className={`text-xs px-2 py-0.5 rounded-full mb-2 ${
                agency.outlook === 'Positive'
                  ? 'bg-data-green/20 text-data-green'
                  : 'bg-gray-600/30 text-gray-400'
              }`}>
                {agency.outlook}
              </div>

              {/* Upgrade Indicator */}
              {agency.change === 'upgrade' ? (
                <div className="flex items-center gap-1 text-data-green">
                  <ArrowUp size={12} />
                  <span className="text-xs font-semibold">+1</span>
                </div>
              ) : agency.change === 'outlook' ? (
                <div className="flex items-center gap-1 text-data-green">
                  <ArrowUp size={12} />
                  <span className="text-xs font-semibold">Outlook</span>
                </div>
              ) : null}

              {/* Date */}
              <div className="text-xs text-gray-500 mt-1">
                {agency.lastUpdate}
              </div>
            </div>
          ))}
        </div>

        {/* Progress to Investment Grade */}
        <div className="mt-4 pt-3 border-t border-terminal-border/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Distance to Investment Grade</span>
            <span className="text-xs font-semibold text-data-amber">1 notch away</span>
          </div>

          {/* Visual Progress Bar */}
          <div className="relative">
            <div className="flex gap-1">
              {['B', 'B+', 'BB-', 'BB', 'BB+', 'BBB-'].map((rating, idx) => (
                <div
                  key={rating}
                  className={`flex-1 h-3 rounded-sm flex items-center justify-center transition-all ${
                    rating === 'BB'
                      ? 'bg-data-green ring-2 ring-data-green/50'
                      : idx < 3
                        ? 'bg-gray-700/50'
                        : rating === 'BBB-'
                          ? 'bg-data-blue/70'
                          : 'bg-data-amber/30'
                  }`}
                >
                  <span className={`text-xs font-bold ${
                    rating === 'BB' ? 'text-white' : rating === 'BBB-' ? 'text-white' : 'text-gray-500'
                  }`}>
                    {rating}
                  </span>
                </div>
              ))}
            </div>

            {/* Labels */}
            <div className="flex justify-between mt-1.5">
              <span className="text-xs text-gray-600">Speculative</span>
              <span className="text-xs text-data-green font-semibold">Current</span>
              <span className="text-xs text-data-blue font-semibold">Investment Grade</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-2 pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.SP} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default CreditRatingsPanel;
